import React from 'react';
import { Plus, Loader2, AlertCircle } from 'lucide-react';
import LibraryHeader from './components/LibraryHeader';
import WorkflowCard from './components/WorkflowCard';
import PausedLeadRow from './components/PausedLeadRow';

/**
 * AutomationLibrary - List view of all automations
 * 
 * Displays automation cards with status toggle functionality
 * and handles loading/empty states.
 * The "Attention Required" tab shows real-time paused/rejected executions.
 */
const AutomationLibrary = ({
  activeLibTab,
  setActiveLibTab,
  workflows,
  workflowMetricsMap = {},
  pausedLeads,
  totalPausedCount = 0,
  pausedLeadsLoading,
  attentionFilter,
  onAttentionFilterChange,
  onResumeRun,
  onCreateNew,
  onCardClick,
  onStatusToggle,
  onDeleteAutomation,
  isLoading,
  isMobile
}) => {
  // Whether any paused/rejected runs exist at all (pre-filter)
  const hasAnyPausedRuns = totalPausedCount > 0;
  // Empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mb-4">
        <Plus className="w-8 h-8 text-orange-400" />
      </div>
      <h3 className="text-lg font-semibold text-stone-700 mb-2">No automations yet</h3>
      <p className="text-sm text-stone-500 text-center mb-6 max-w-sm">
        Create your first automation to start nurturing leads automatically.
      </p>
      <button
        onClick={onCreateNew}
        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium text-sm transition-colors"
      >
        Create Automation
      </button>
    </div>
  );

  // Loading state component
  const LoadingState = () => (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
    </div>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <LibraryHeader
          activeLibTab={activeLibTab}
          setActiveLibTab={setActiveLibTab}
          onCreateNew={onCreateNew}
          isMobile={isMobile}
          pausedCount={totalPausedCount}
        />

        <div className="flex-1 p-5 pb-24">
          {activeLibTab === 'active' ? (
            isLoading ? (
              <LoadingState />
            ) : workflows.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {workflows.map((workflow) => (
                  <WorkflowCard
                    key={workflow.id}
                    workflow={{
                      ...workflow,
                      leadCount: workflowMetricsMap[workflow.id]?.leadCount ?? 0,
                      completedCount: workflowMetricsMap[workflow.id]?.completedCount ?? 0,
                    }}
                    onCardClick={onCardClick}
                    onStatusToggle={onStatusToggle}
                    onDeleteAutomation={onDeleteAutomation}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            )
          ) : pausedLeadsLoading ? (
            <LoadingState />
          ) : !hasAnyPausedRuns ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-stone-700 mb-2">All clear!</h3>
              <p className="text-sm text-stone-500 text-center max-w-sm">
                No automations need attention right now.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-notion-border rounded-xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-notion-border bg-stone-50/50 flex flex-col gap-2">
                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Attention Required</h3>
                <select
                  className="text-xs bg-white border border-notion-border rounded px-2 py-1.5 outline-none text-stone-600 w-full"
                  value={attentionFilter}
                  onChange={(e) => onAttentionFilterChange(e.target.value)}
                >
                  <option value="all">Filter by: All</option>
                  <option value="paused_only">Paused Only (Resumable)</option>
                  <option value="stopped_only">Stopped Only (Permanent)</option>
                  <option value="lead_replied">Lead Replied</option>
                  <option value="manual">Manually Stopped</option>
                  <option value="status_change">Status Changed</option>
                  <option value="no_property">No Interested Property</option>
                </select>
              </div>
              <div className="divide-y divide-notion-border">
                {pausedLeads.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-stone-400">
                    No leads match this filter.
                  </div>
                ) : (
                  pausedLeads.map((lead) => (
                    <PausedLeadRow key={lead.id} lead={lead} isMobile={isMobile} onResume={onResumeRun} />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <LibraryHeader
        activeLibTab={activeLibTab}
        setActiveLibTab={setActiveLibTab}
        onCreateNew={onCreateNew}
        isMobile={isMobile}
        pausedCount={totalPausedCount}
      />

      <div className="flex-1 overflow-y-auto bg-stone-50/50 p-8">
        {activeLibTab === 'active' ? (
          isLoading ? (
            <LoadingState />
          ) : workflows.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workflows.map((workflow) => (
                <WorkflowCard
                  key={workflow.id}
                  workflow={{
                    ...workflow,
                    leadCount: workflowMetricsMap[workflow.id]?.leadCount ?? 0,
                    completedCount: workflowMetricsMap[workflow.id]?.completedCount ?? 0,
                  }}
                  onCardClick={onCardClick}
                  onStatusToggle={onStatusToggle}
                  onDeleteAutomation={onDeleteAutomation}
                  isMobile={isMobile}
                />
              ))}
            </div>
          )
        ) : pausedLeadsLoading ? (
          <LoadingState />
        ) : !hasAnyPausedRuns ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-stone-700 mb-2">All clear!</h3>
            <p className="text-sm text-stone-500 text-center max-w-sm">
              No automations need attention right now.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-notion-border rounded-lg overflow-hidden shadow-card">
            <div className="p-4 bg-stone-50 border-b border-notion-border flex items-center justify-between">
              <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Attention Required</h3>
              <select
                className="text-xs bg-white border border-notion-border rounded px-2 py-1.5 outline-none text-stone-600"
                value={attentionFilter}
                onChange={(e) => onAttentionFilterChange(e.target.value)}
              >
                <option value="all">Filter by: All</option>
                <option value="paused_only">Paused Only (Resumable)</option>
                <option value="stopped_only">Stopped Only (Permanent)</option>
                <option value="lead_replied">Lead Replied</option>
                <option value="manual">Manually Stopped</option>
                <option value="status_change">Status Changed</option>
                <option value="no_property">No Interested Property</option>
              </select>
            </div>
            <table className="w-full text-left">
              <thead className="bg-stone-50 text-[10px] font-semibold text-stone-400 uppercase tracking-wider border-b border-notion-border">
                <tr>
                  <th className="p-4">Lead</th>
                  <th className="p-4">Workflow</th>
                  <th className="p-4">Reason</th>
                  <th className="p-4">When</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-xs text-stone-700 divide-y divide-notion-border">
                {pausedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-stone-400">
                      No leads match this filter.
                    </td>
                  </tr>
                ) : (
                  pausedLeads.map((lead) => (
                    <PausedLeadRow key={lead.id} lead={lead} isMobile={isMobile} onResume={onResumeRun} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationLibrary;
